import { Link } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { ChevronRight } from 'lucide-react'
import { pageLink, pagePanel, pagePanelBody, pagePanelHeader } from '../layout/page-styles'

interface DashboardSectionProps {
  title: string
  subtitle?: string
  icon?: React.ReactNode
  viewAllTo?: string
  viewAllLabel?: string
  children: React.ReactNode
  className?: string
}

export function DashboardSection({
  title,
  subtitle,
  icon,
  viewAllTo,
  viewAllLabel,
  children,
  className,
}: DashboardSectionProps) {
  return (
    <div className={cn(pagePanel, className)}>
      <div className={cn(pagePanelHeader, 'flex items-center justify-between')}>
        <div className='flex items-center gap-2.5'>
          {icon}
          <div>
            <h2 className='text-base font-semibold text-neutral-900'>{title}</h2>
            {subtitle && <p className='text-xs text-neutral-500 mt-0.5'>{subtitle}</p>}
          </div>
        </div>
        {viewAllTo && viewAllLabel ? (
          <Link to={viewAllTo} className={cn(pageLink, 'inline-flex items-center gap-0.5')}>
            {viewAllLabel}
            <ChevronRight className='w-4 h-4' />
          </Link>
        ) : null}
      </div>
      <div className={pagePanelBody}>{children}</div>
    </div>
  )
}

interface DashboardLinkRowProps {
  to: string
  icon: React.ReactNode
  title: string
  subtitle?: string
  actionLabel?: string
  className?: string
}

export function DashboardLinkRow({
  to,
  icon,
  title,
  subtitle,
  actionLabel,
  className,
}: DashboardLinkRowProps) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center justify-between gap-4 p-4 rounded-xl border border-primary-100 bg-surface-secondary/60',
        'hover:bg-surface-card-hover transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2',
        className
      )}
    >
      <div className='flex items-center gap-3 min-w-0'>
        {icon}
        <div className='min-w-0'>
          <div className='text-sm font-semibold text-neutral-900'>{title}</div>
          {subtitle ? <div className='text-sm text-neutral-600 truncate mt-0.5'>{subtitle}</div> : null}
        </div>
      </div>
      <span className={cn('flex-shrink-0 inline-flex items-center gap-0.5', pageLink)}>
        {actionLabel}
        <ChevronRight className='w-4 h-4' />
      </span>
    </Link>
  )
}

interface DashboardModuleCardProps {
  to: string
  title: string
  assigned: number
  completed: number
  percent: number
  labels: {
    active: string
    completed: string
    open: string
    viewModule: string
  }
}

export function DashboardModuleCard({
  to,
  title,
  assigned,
  completed,
  percent,
  labels,
}: DashboardModuleCardProps) {
  const open = Math.max(assigned - completed, 0)

  return (
    <Link
      to={to}
      className={cn(
        'block rounded-xl border border-primary-100 bg-surface-secondary/60 p-4',
        'hover:bg-surface-card-hover hover:border-primary-200 transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2'
      )}
    >
      <div className='flex items-start justify-between gap-3'>
        <p className='text-sm font-semibold text-neutral-900 truncate'>{title}</p>
        <span className='text-sm font-bold text-primary-700 flex-shrink-0'>{percent}%</span>
      </div>

      <div className='mt-3 grid grid-cols-3 gap-3'>
        <div>
          <div className='text-[11px] font-medium uppercase tracking-wide text-neutral-500'>
            {labels.active}
          </div>
          <div className='mt-0.5 text-lg font-bold text-neutral-900'>{assigned}</div>
        </div>
        <div>
          <div className='text-[11px] font-medium uppercase tracking-wide text-neutral-500'>
            {labels.completed}
          </div>
          <div className='mt-0.5 text-lg font-bold text-neutral-900'>{completed}</div>
        </div>
        <div>
          <div className='text-[11px] font-medium uppercase tracking-wide text-neutral-500'>
            {labels.open}
          </div>
          <div className='mt-0.5 text-lg font-bold text-neutral-900'>{open}</div>
        </div>
      </div>

      <div className='mt-3 h-1.5 w-full rounded-full bg-primary-50 overflow-hidden'>
        <div
          className='h-full rounded-full bg-gradient-to-r from-primary-300 to-primary-500 transition-all'
          style={{ width: `${percent}%` }}
        />
      </div>

      <span className={cn('mt-3 inline-flex items-center gap-0.5', pageLink)}>
        {labels.viewModule}
        <ChevronRight className='w-4 h-4' />
      </span>
    </Link>
  )
}
